import { Checkbox, classNames, Tooltip } from '@cma/common'
import { HelixIcon } from '@helix/helix-icon'
import pluralize from 'pluralize'
import { getLocationIcon } from './utils'
import { OrgOption } from './types'

interface OrganizationItemProps {
  item: OrgOption
  truncateLabel?: boolean
  isGroup?: boolean
  isChecked?: boolean
  isActive?: boolean | null
  onClick?: () => void
  onCheckChange: (checked: boolean) => void
}

export function OrganizationItem({
  isActive = null,
  isGroup = false,
  truncateLabel = true,
  item,
  isChecked,
  onClick,
  onCheckChange
}: OrganizationItemProps) {
  return (
    <div
      className={classNames(
        'helix-border-radius--sm group m-auto flex h-[52px] w-full max-w-[384px] items-center justify-between gap-2 px-3',
        {
          'flex-1': isGroup,
          '!border !border-solid !border-gray-300 hover:!border-transparent':
            isActive !== null || isActive === false,
          '!border !border-solid !border-brand-light bg-brand-white hover:!border-transparent':
            isActive,
          'hover:bg-gray-100 hover:shadow-raised': !item.isDisabled,
          'bg-gray-100': item.isDisabled
        }
      )}>
      <div
        role="button"
        tabIndex={0}
        className="helix-d-flex helix-align--middle helix-gap-3 min-w-0 flex-1 cursor-pointer"
        onClick={(e) => {
          e.stopPropagation()
          onClick?.()
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            onClick?.()
          }
        }}>
        <HelixIcon
          icon={() => getLocationIcon(item.value.type)}
          className="helix-svg-fill--green-500 flex-shrink-0"
        />
        <div className="helix-d-flex helix-flex--column min-w-0 flex-1">
          {item.label.length > 17 && truncateLabel ? (
            <Tooltip content={item.label}>
              <span
                className="helix-body--strong max-w-[160px] truncate"
                title={item.label}>
                {item.label}
              </span>
            </Tooltip>
          ) : (
            <span className="helix-body--strong" title={item.label}>
              {item.label}
            </span>
          )}
          <span
            className="helix-small truncate"
            title={`${item.value.usersCount} ${pluralize(
              'user',
              item.value.usersCount
            )}`}>
            {item.value.usersCount} {pluralize('user', item.value.usersCount)}
          </span>
        </div>
      </div>
      <div
        className={classNames('flex-shrink-0 items-center transition-opacity', {
          'hidden group-hover:flex': !isChecked && !item.isDisabled,
          hidden: item.isDisabled,
          flex: isChecked
        })}>
        <div
          className={classNames({
            'cursor-pointer [&_*]:cursor-pointer': !item.isDisabled,
            'cursor-not-allowed opacity-50 [&_*]:cursor-not-allowed':
              item.isDisabled
          })}>
          <Checkbox
            checked={isChecked}
            disabled={item.isDisabled}
            onClick={(e) => {
              e.stopPropagation()
              if (item.isDisabled) e.preventDefault()
            }}
            onChange={(e) => {
              if (item.isDisabled) {
                e.preventDefault()
                return
              }
              e.stopPropagation()
              onCheckChange(e.target.checked)
            }}
          />
        </div>
      </div>
    </div>
  )
}
